<template>
  <transition name="ai-chat-slide">
  <v-card v-show="visible" id="ai-chat-card">
    <!-- Header -->
    <div
      class="chat-header pa-3"
      @mousedown="onDragStart"
      @touchstart.passive="onDragStart"
      :style="{ cursor: isDragging ? 'grabbing' : 'grab' }"
    >
      <div class="d-flex align-center">
        <div class="header-avatar mr-2">
          <v-img
            src="../../assets/felix/icon/Icone_SemFundo.svg"
            width="32"
            height="32"
            contain
          ></v-img>
        </div>
        <div class="d-flex flex-column">
          <div class="d-flex align-center">
            <span class="subtitle-2 font-weight-bold">Felix</span>
            <v-chip x-small color="primary" class="ml-2" style="height: 16px; font-size: 9px;">BETA</v-chip>
          </div>
          <span class="overline" style="font-size: 10px !important; line-height: 1.2; opacity: 0.6;">
            {{ aiUsage?.messageCount !== undefined ? aiUsage.messageCount + '/' + aiUsage.limite + ' mensagens' : '' }}
          </span>
        </div>

        <v-spacer></v-spacer>

        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn icon x-small class="mr-1" @click="startNewSession" v-on="on">
              <v-icon size="18">mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>Nova conversa</span>
        </v-tooltip>

        <v-menu v-model="showSessions" offset-y left max-height="400" :close-on-content-click="false">
          <template #activator="{ on, attrs }">
            <v-tooltip bottom>
              <template #activator="{ on: tipOn }">
                <v-btn icon x-small class="mr-1" v-bind="attrs" v-on="{ ...on, ...tipOn }">
                  <v-icon size="18">mdi-history</v-icon>
                </v-btn>
              </template>
              <span>Histórico</span>
            </v-tooltip>
          </template>
          <v-list dense style="max-width: 280px; max-height: 350px; overflow-y: auto;">
            <v-subheader class="text-uppercase" style="font-size: 10px;">Conversas anteriores</v-subheader>
            <v-list-item
              v-for="chat in chatHistory[getCurrentTabType()]"
              :key="chat.id"
              @click="loadSession({ chatId: chat.id, title: chat.title })"
              class="history-item"
            >
              <v-list-item-icon class="mr-2 my-auto">
                <v-icon small color="grey">mdi-message-text-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-truncate body-2">
                  {{ chat.title }}
                </v-list-item-title>
                <v-list-item-subtitle style="font-size: 10px;">
                  {{ formatRelativeDate(chat.lastMessageAt || chat.createdAt) }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="chatHistory[getCurrentTabType()].length === 0">
              <v-list-item-content>
                <v-list-item-title class="text-center grey--text body-2">
                  Nenhuma conversa
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-tooltip v-if="userLogged && (userLogged.mainType === 0 || userLogged.mainType === 1)" bottom>
          <template #activator="{ on }">
            <v-btn icon x-small class="mr-1" @click="openVectorizationPanel" v-on="on">
              <v-icon size="18">mdi-database-cog-outline</v-icon>
            </v-btn>
          </template>
          <span>Status dos documentos IA</span>
        </v-tooltip>

        <v-btn icon x-small @click="$emit('close')">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Tabs -->
      <div class="segmented-tabs mt-2">
        <button
          v-for="(tab, i) in visibleTabItems"
          :key="tab.key"
          class="seg-tab"
          :class="{ 'seg-tab--active': activeTab === tab.index }"
          @click="activeTab = tab.index; onTabChange()"
        >
          <v-icon size="14" :color="activeTab === i ? '#fff' : '#666'" class="mr-1">{{ tab.icon }}</v-icon>
          {{ tab.label }}
        </button>
      </div>
    </div>

    <!-- Messages Area -->
    <div id="ai-messages" ref="messagesContainer">
      <!-- Empty State / Welcome -->
      <div
        v-if="currentMessages.length === 0 && !isProcessing"
        class="empty-state d-flex flex-column align-center justify-center"
      >
        <div class="felix-intro-container mb-3">
          <video
            :src="felixVideos.presentation"
            width="180"
            height="180"
            autoplay
            loop
            muted
            playsinline
            class="felix-intro-video"
            @error="showIntroVideo = false"
            @loadeddata="onIntroVideoLoaded"
          ></video>
        </div>
        <div class="text-center px-6">
          <div class="subtitle-1 font-weight-bold mb-1">{{ getTabWelcomeTitle() }}</div>
          <div class="caption grey--text text--darken-1 mb-4" style="line-height: 1.4;">
            {{ getTabWelcomeMessage() }}
          </div>
        </div>

        <!-- Suggestion chips -->
        <div class="d-flex flex-column align-center px-4" style="gap: 8px; width: 100%;">
          <v-btn
            v-for="suggestion in getTabSuggestions()"
            :key="suggestion"
            outlined
            small
            rounded
            color="primary"
            class="suggestion-btn text-none"
            @click="useSuggestion(suggestion)"
          >
            <v-icon left x-small>mdi-arrow-right</v-icon>
            {{ suggestion }}
          </v-btn>
        </div>
      </div>

      <!-- Messages -->
      <div v-else class="messages-list pa-3">
        <template v-for="(message, index) in currentMessages">
          <AIUserMessage
            v-if="message.role === 'User'"
            :key="message.id"
            :message="message"
          />
          <AIAssistantMessage
            v-else-if="message.role === 'Assistant' && (!message.isStreaming || message.content)"
            :key="message.id"
            :message="message"
            :isStreaming="!!message.isStreaming"
            :felixVideos="felixVideos"
            :felixStateKey="felixStateKeyForMessages"
            :showAvatar="shouldShowAvatar(index)"
            @retry="retryMessage"
          />
        </template>

        <!-- Tool status indicator -->
        <div v-if="isProcessing && currentToolStatus && !hasStreamingContent" class="tool-status-row d-flex align-center ml-1 my-2">
          <div class="tool-status-bubble d-flex align-center">
            <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2"></v-progress-circular>
            <span class="caption primary--text">{{ currentToolStatus }}</span>
          </div>
        </div>

        <!-- Pensando indicator -->
        <div v-else-if="isProcessing && !hasStreamingContent && !currentToolStatus" class="thinking-row d-flex align-center ml-1 my-2">
          <div class="thinking-bubble d-flex align-center">
            <div class="typing-dots mr-2">
              <span></span><span></span><span></span>
            </div>
            <span class="caption grey--text text--darken-1">
              Pensando<span v-if="elapsedSeconds > 3" style="opacity: 0.6;"> ({{ elapsedSeconds }}s)</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Scroll to bottom -->
    <v-fab-transition>
      <v-btn
        v-show="showScrollToBottom"
        fab
        x-small
        color="white"
        class="scroll-bottom-fab"
        elevation="2"
        @click="scrollToBottom(true)"
      >
        <v-icon small color="grey darken-2">mdi-chevron-double-down</v-icon>
      </v-btn>
    </v-fab-transition>

    <!-- Input Area -->
    <div class="chat-input-area px-3 pb-3 pt-2">
      <v-alert
        v-if="aiUsage.canUseAi === false"
        type="warning"
        dense
        text
        class="mb-2 body-2"
        icon="mdi-alert-circle-outline"
      >
        Limite de mensagens atingido.
      </v-alert>

      <div class="input-wrapper d-flex align-end">
        <v-textarea
          ref="messageInput"
          v-model="currentMessage"
          :placeholder="getTabPlaceholder()"
          outlined
          auto-grow
          rows="1"
          row-height="20"
          dense
          hide-details
          class="chat-textarea"
          :disabled="isProcessing || aiUsage.canUseAi === false"
          @keydown.enter.exact.prevent="sendMessage"
          @input="detectUserTyping"
        ></v-textarea>
        <v-btn
          fab
          x-small
          elevation="0"
          color="primary"
          class="send-btn ml-2"
          :loading="isProcessing"
          :disabled="!currentMessage || !currentMessage.trim() || isProcessing || aiUsage.canUseAi === false"
          @click="sendMessage"
        >
          <v-icon small color="white">mdi-send</v-icon>
        </v-btn>
      </div>

      <div class="text-center mt-1">
        <span class="overline grey--text" style="font-size: 9px !important;">
          Felix pode cometer erros. Verifique informações importantes.
        </span>
      </div>
    </div>
    <!-- Vectorization Status Panel -->
    <v-dialog v-model="showVectorizationPanel" max-width="480" scrollable>
      <v-card class="vec-panel" rounded="lg">
        <!-- Header -->
        <div class="vec-panel-header">
          <div class="d-flex align-center">
            <div class="vec-header-icon mr-2">
              <v-icon size="18" color="white">mdi-brain</v-icon>
            </div>
            <div>
              <div class="subtitle-2 font-weight-bold" style="line-height: 1.2;">Documentos IA</div>
              <div class="caption" style="opacity: 0.7; line-height: 1.2;">{{ vecStatus.total }} documento(s) cadastrado(s)</div>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon x-small @click="refreshActiveVecStatus" :loading="vecLoading" class="vec-header-btn mr-1">
              <v-icon size="16">mdi-refresh</v-icon>
            </v-btn>
            <v-btn icon x-small @click="showVectorizationPanel = false" class="vec-header-btn">
              <v-icon size="16">mdi-close</v-icon>
            </v-btn>
          </div>
          <!-- Doc type tabs -->
          <div class="vec-segmented-tabs mt-2">
            <button
              class="vec-seg-tab"
              :class="{ 'vec-seg-tab--active': vecDocType === 0 }"
              @click="switchVecDocType(0)"
            >
              POPs
              <span v-if="popVec.status.total > 0" class="vec-seg-badge">{{ popVec.status.total }}</span>
            </button>
            <button
              class="vec-seg-tab"
              :class="{ 'vec-seg-tab--active': vecDocType === 1 }"
              @click="switchVecDocType(1)"
            >
              Manuais
              <span v-if="manualVec.status.total > 0" class="vec-seg-badge">{{ manualVec.status.total }}</span>
            </button>
          </div>
        </div>

        <!-- Summary cards -->
        <div class="vec-summary">
          <div class="vec-stat-card vec-stat--completed">
            <div class="vec-stat-number">{{ vecStatus.completed }}</div>
            <div class="vec-stat-label">Prontos</div>
          </div>
          <div class="vec-stat-card vec-stat--processing">
            <div class="vec-stat-number">{{ vecStatus.processing + vecStatus.pending }}</div>
            <div class="vec-stat-label">Em processo</div>
          </div>
          <div class="vec-stat-card vec-stat--failed">
            <div class="vec-stat-number">{{ vecStatus.failed }}</div>
            <div class="vec-stat-label">Erro</div>
          </div>
          <div class="vec-stat-card vec-stat--none">
            <div class="vec-stat-number">{{ vecStatus.none }}</div>
            <div class="vec-stat-label">Pendentes</div>
          </div>
          <div class="vec-stat-card vec-stat--notsupported">
            <div class="vec-stat-number">{{ vecStatus.notSupported }}</div>
            <div class="vec-stat-label">Não suportado</div>
          </div>
        </div>

        <!-- Currently processing indicator -->
        <div v-if="currentlyProcessingDoc" class="px-4 pt-1 pb-1">
          <div class="vec-current-processing d-flex align-center">
            <v-progress-circular indeterminate size="14" width="2" color="blue darken-1" class="mr-2"></v-progress-circular>
            <div style="min-width: 0; flex: 1;">
              <div class="caption font-weight-medium blue--text text--darken-1" style="line-height: 1.2;">Processando agora:</div>
              <div class="caption" style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 1.3;">{{ currentlyProcessingDoc.name }}</div>
            </div>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="px-4 pb-1 d-flex" style="gap: 6px;">
          <v-btn v-if="vecStatus.none > 0 || vecStatus.failed > 0" small depressed color="primary" :loading="vecReprocessing" @click="reprocessDocuments" class="text-none flex-grow-1" style="border-radius: 8px; letter-spacing: 0;">
            <v-icon small left>mdi-play-circle-outline</v-icon>
            Processar {{ vecStatus.none + vecStatus.failed }} pendente(s)
          </v-btn>
          <v-btn v-if="vecStatus.processing > 0 || vecStatus.pending > 0" small depressed color="red" dark :loading="vecResetting" @click="resetProcessing" class="text-none flex-grow-1" style="border-radius: 8px; letter-spacing: 0;">
            <v-icon small left>mdi-stop-circle-outline</v-icon>
            Cancelar {{ vecStatus.processing + vecStatus.pending }} em processo
          </v-btn>
        </div>

        <!-- Filter tabs (segmented style) -->
        <div class="px-4 py-2">
          <div class="vec-segmented-tabs">
            <button
              v-for="(filter, i) in vecFilterOptions"
              :key="filter.key"
              class="vec-seg-tab"
              :class="{ 'vec-seg-tab--active': vecFilter === i }"
              @click="vecFilter = i"
            >
              {{ filter.label }}
              <span v-if="filter.count > 0" class="vec-seg-badge">{{ filter.count }}</span>
            </button>
          </div>
        </div>

        <!-- Document list -->
        <div class="vec-doc-list">
          <div v-if="vecLoading" class="d-flex justify-center py-6">
            <v-progress-circular indeterminate size="28" width="2" color="primary"></v-progress-circular>
          </div>
          <template v-else>
            <div
              v-for="doc in filteredVecDocuments"
              :key="doc.id"
              class="vec-doc-row"
            >
              <div class="vec-doc-icon" :class="'vec-doc-icon--s' + doc.status">
                <v-progress-circular v-if="doc.status === 2" indeterminate size="14" width="2" color="blue"></v-progress-circular>
                <v-icon v-else size="14" :color="vecStatusColor(doc.status)">{{ vecStatusIcon(doc.status) }}</v-icon>
              </div>
              <div class="vec-doc-info">
                <div class="vec-doc-name">{{ doc.name }}</div>
                <div class="vec-doc-meta">.{{ doc.extension }} · {{ vecStatusLabel(doc.status) }}</div>
              </div>
              <v-tooltip v-if="doc.status === 0 || doc.status === 1" bottom>
                <template #activator="{ on }">
                  <v-btn icon x-small v-on="on" @click="vectorizeSingleDoc(doc)" :loading="doc._loading">
                    <v-icon size="14" color="primary">mdi-play-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Processar documento</span>
              </v-tooltip>
              <v-tooltip v-else-if="doc.status === 4" bottom>
                <template #activator="{ on }">
                  <v-btn icon x-small v-on="on" @click="vectorizeSingleDoc(doc)" :loading="doc._loading">
                    <v-icon size="14" color="red">mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Reprocessar documento</span>
              </v-tooltip>
              <v-tooltip v-else-if="doc.status === 3" bottom>
                <template #activator="{ on }">
                  <v-btn icon x-small v-on="on" @click="vectorizeSingleDoc(doc)" :loading="doc._loading">
                    <v-icon size="14" color="grey">mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Reprocessar documento</span>
              </v-tooltip>
            </div>
            <div v-if="filteredVecDocuments.length === 0" class="text-center py-6">
              <v-icon size="32" color="grey lighten-1" class="mb-2">mdi-file-search-outline</v-icon>
              <div class="caption grey--text">Nenhum documento nesta categoria</div>
            </div>
          </template>
        </div>
      </v-card>
    </v-dialog>
  </v-card>
  </transition>
</template>

<style scoped>
/* ===== Chat Transition ===== */
.ai-chat-slide-enter-active,
.ai-chat-slide-leave-active {
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.ai-chat-slide-enter,
.ai-chat-slide-leave-to {
  transform: translateY(20px) scale(0.95);
  opacity: 0;
}

/* ===== Card Shell ===== */
#ai-chat-card {
  display: flex;
  flex-direction: column;
  position: fixed;
  z-index: 10 !important;
  bottom: 12px;
  right: 12px;
  min-width: 400px;
  max-width: 480px;
  height: 85vh;
  max-height: 700px;
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  overflow: hidden;
  background: #fafafa;
}

#ai-chat-card.snap-animating {
  transition: top 0.4s cubic-bezier(0.4, 0, 0.2, 1),
              left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

#ai-chat-card.is-dragging {
  transition: none;
  user-select: none;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.18), 0 4px 12px rgba(0, 0, 0, 0.12) !important;
}

#ai-chat-card[style*="display: none"] {
  display: none !important;
}

/* ===== Header ===== */
.chat-header {
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}

.header-avatar {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
}

/* ===== Segmented Tabs ===== */
.segmented-tabs {
  display: flex;
  background: #f0f0f0;
  border-radius: 12px;
  padding: 3px;
  gap: 2px;
}

.seg-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 7px 10px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #888;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
  white-space: nowrap;
  position: relative;
}

.seg-tab:hover:not(.seg-tab--active) {
  background: rgba(0, 0, 0, 0.05);
  color: #444;
}

.seg-tab--active {
  background: #0F3565;
  color: #fff;
  box-shadow: 0 2px 8px rgba(15, 53, 101, 0.35);
  font-weight: 600;
}

/* ===== Messages Area ===== */
#ai-messages {
  flex: 1;
  overflow-y: auto;
  scroll-behavior: smooth;
  position: relative;
}

#ai-messages::-webkit-scrollbar {
  width: 5px;
}

#ai-messages::-webkit-scrollbar-thumb {
  border-radius: 10px;
  background-color: rgba(0, 0, 0, 0.12);
}

#ai-messages::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.25);
}

.messages-list {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* ===== Empty State ===== */
.empty-state {
  min-height: 100%;
  padding: 24px 16px;
}

.felix-intro-video {
  width: 180px;
  height: 180px;
  object-fit: contain;
}

.felix-intro-container:hover .felix-intro-video {
  transform: scale(1.03);
  transition: transform 0.3s ease;
}

.suggestion-btn {
  max-width: 420px;
  width: 100%;
  justify-content: flex-start;
  font-size: 12px !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
  background: #fff !important;
  white-space: normal !important;
  height: auto !important;
  min-height: 32px;
  padding: 8px 16px !important;
  line-height: 1.4;
  text-align: left;
}

.suggestion-btn.v-size--small {
  height: auto !important;
}

.suggestion-btn ::v-deep .v-btn__content {
  white-space: normal !important;
  text-align: left;
  flex: 1;
}

.suggestion-btn:hover {
  background: #f5f5f5 !important;
  border-color: currentColor !important;
}

/* ===== Thinking / Tool Status ===== */
.thinking-bubble,
.tool-status-bubble {
  background: #fff;
  border-radius: 16px;
  padding: 8px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.typing-dots {
  display: flex;
  gap: 3px;
  align-items: center;
}

.typing-dots span {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #999;
  animation: typingDot 1.4s infinite;
}

.typing-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typingDot {
  0%, 60%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  30% {
    opacity: 1;
    transform: scale(1);
  }
}

/* ===== Scroll FAB ===== */
.scroll-bottom-fab {
  position: absolute;
  bottom: 80px;
  right: 16px;
  z-index: 5;
  width: 28px !important;
  height: 28px !important;
}

/* ===== Input Area ===== */
.chat-input-area {
  background: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}

.input-wrapper {
  gap: 0;
}

.chat-textarea {
  font-size: 13px;
}

.chat-textarea ::v-deep .v-input__slot {
  border-radius: 20px !important;
  min-height: 36px !important;
  padding: 4px 14px !important;
  background: #f5f5f5 !important;
  border-color: transparent !important;
}

.chat-textarea ::v-deep .v-input__slot:hover {
  background: #eeeeee !important;
}

.chat-textarea ::v-deep .v-input__slot fieldset {
  border-color: transparent !important;
}

.chat-textarea ::v-deep .v-input--is-focused .v-input__slot {
  background: #fff !important;
}

.chat-textarea ::v-deep .v-input--is-focused .v-input__slot fieldset {
  border-color: var(--v-primary-base) !important;
  border-width: 1.5px;
}

.chat-textarea ::v-deep textarea {
  max-height: 100px;
  overflow-y: auto !important;
  font-size: 13px;
  line-height: 1.4;
  margin-top: 4px !important;
}

.send-btn {
  width: 36px !important;
  height: 36px !important;
  margin-bottom: 3px;
  flex-shrink: 0;
}

/* ===== History ===== */
.history-item {
  min-height: 42px !important;
}

.history-item:hover {
  background: #f5f5f5;
}

/* ===== Animations ===== */
.rotating-icon {
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* ===== Vectorization Panel ===== */
.vec-panel {
  overflow: hidden;
}

.vec-panel-header {
  background: linear-gradient(135deg, #0F3565, #1a4a80);
  color: #fff;
  padding: 14px 16px;
}

.vec-header-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}

.vec-header-btn.v-btn {
  color: rgba(255, 255, 255, 0.85) !important;
  background: rgba(255, 255, 255, 0.1) !important;
  border-radius: 8px;
}

.vec-header-btn.v-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

.vec-summary {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  padding: 12px 16px;
}

.vec-stat-card {
  text-align: center;
  padding: 10px 4px;
  border-radius: 10px;
}

.vec-stat-number {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
}

.vec-stat-label {
  font-size: 10px;
  font-weight: 500;
  opacity: 0.8;
  margin-top: 2px;
}

.vec-stat--completed {
  background: #e8f5e9;
  color: #2e7d32;
}

.vec-stat--processing {
  background: #e3f2fd;
  color: #1565c0;
}

.vec-stat--failed {
  background: #fce4ec;
  color: #c62828;
}

.vec-stat--none {
  background: #f5f5f5;
  color: #616161;
}

.vec-stat--notsupported {
  background: #fff3e0;
  color: #e65100;
}

/* Vec segmented filter */
.vec-segmented-tabs {
  display: flex;
  background: #f0f0f0;
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
}

.vec-seg-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 5px 6px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #888;
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
  white-space: nowrap;
}

.vec-seg-tab:hover:not(.vec-seg-tab--active) {
  background: rgba(0, 0, 0, 0.05);
  color: #444;
}

.vec-seg-tab--active {
  background: #0F3565;
  color: #fff;
  box-shadow: 0 1px 4px rgba(15, 53, 101, 0.3);
  font-weight: 600;
}

.vec-seg-badge {
  font-size: 9px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  padding: 1px 5px;
  font-weight: 600;
  line-height: 1.4;
}

.vec-seg-tab--active .vec-seg-badge {
  background: rgba(255, 255, 255, 0.2);
}

/* Vec document list */
.vec-doc-list {
  max-height: 320px;
  overflow-y: auto;
  padding: 0 8px 8px;
}

.vec-doc-list::-webkit-scrollbar {
  width: 4px;
}

.vec-doc-list::-webkit-scrollbar-thumb {
  border-radius: 10px;
  background-color: rgba(0, 0, 0, 0.1);
}

.vec-doc-row {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-radius: 8px;
  transition: background 0.15s ease;
}

.vec-doc-row:hover {
  background: #f8f8f8;
}

.vec-doc-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 10px;
}

.vec-doc-icon--s0 { background: #f5f5f5; }
.vec-doc-icon--s1 { background: #fff3e0; }
.vec-doc-icon--s2 { background: #e3f2fd; }
.vec-doc-icon--s3 { background: #e8f5e9; }
.vec-doc-icon--s4 { background: #fce4ec; }
.vec-doc-icon--s5 { background: #f5f5f5; }

.vec-doc-info {
  flex: 1;
  min-width: 0;
}

.vec-doc-name {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vec-doc-meta {
  font-size: 10px;
  color: #999;
  margin-top: 1px;
}

.vec-current-processing {
  background: #e3f2fd;
  border-radius: 8px;
  padding: 8px 10px;
  border: 1px solid #bbdefb;
}
</style>

<script>
import AIUserMessage from './AIUserMessage.vue'
import AIAssistantMessage from './AIAssistantMessage.vue'
import AIChatService from '../../services/AIChatService'
import FelixNeutral from '../../assets/felix/body1/Neutro_Corpo_1.png'
import FelixThinking from '../../assets/felix/body1/OlhandoPraBaixo_Corpo_1.png'
import FelixRelieved from '../../assets/felix/body1/Aliviado_Corpo_1.png'
import FelixScared from '../../assets/felix/body1/Assustado_Corpo_1.png'
import FelixDisappointed from '../../assets/felix/body1/Decepcionado_Corpo_1.png'
import FelixHappy from '../../assets/felix/body1/Satisfeito_Corpo_1.png'
import FelixLaughing from '../../assets/felix/body1/Gargalhada_Corpo_1.png'
import FelixAngry from '../../assets/felix/body1/Raiva_Corpo_1.png'
import FelixSad from '../../assets/felix/body1/Triste_Corpo_1.png'
import ApiService from '../../services/ApiService'
import moment from 'moment'
import 'moment/locale/pt-br'
moment.locale('pt-br')

export default {
  name: 'AIChat',

  components: {
    AIUserMessage,
    AIAssistantMessage,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      institution: null,
      isIdle: false,
      idleTimeout: null,
      idleMs: 30000,
      typingTimeout: null,
      showSessions: false,
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      cardPosition: null,
      showVectorizationPanel: false,
      vecDocType: 0,
      popVec: { loading: false, reprocessing: false, resetting: false, status: { total: 0, pending: 0, processing: 0, completed: 0, failed: 0, notSupported: 0, none: 0, documents: [] } },
      manualVec: { loading: false, reprocessing: false, resetting: false, status: { total: 0, pending: 0, processing: 0, completed: 0, failed: 0, notSupported: 0, none: 0, documents: [] } },
      vecFilter: 0,
      currentMessage: '',
      activeTab: parseInt(localStorage.getItem('felix_activeTab') || '0', 10),
      sessions: {
        sistema: { messages: [], chatId: 0 },
        manuais: { messages: [], chatId: 0 },
        pops: { messages: [], chatId: 0 },
      },
      chatHistory: {
        sistema: [],
        manuais: [],
        pops: [],
      },
      loadingChats: false,
      apiService: new ApiService(),
      aiUsage: { limite: '-', messageCount: '-' },
      isProcessing: false,
      lastProcessingTime: null,
      chatService: new AIChatService(),
      felixVideos: {
        presentation: 'https://equipacarefix.blob.core.windows.net/public/00e1a52bb3be49f781d0f3da7f42d9f3.webm',
        neutral: 'https://equipacarefix.blob.core.windows.net/public/473b3417869d4f08bf832594c2982bdd.webm',
        searching: 'https://equipacarefix.blob.core.windows.net/public/7daee154de5141ab8c8385beee1b961b.webm',
        sleeping: 'https://equipacarefix.blob.core.windows.net/public/3d3faf6215d347ecab939cbeea34fa76.webm',
      },
      showIntroVideo: true,
      felixExpressions: {
        neutral: FelixNeutral,
        thinking: FelixThinking,
        relieved: FelixRelieved,
        scared: FelixScared,
        disappointed: FelixDisappointed,
        happy: FelixHappy,
        laughing: FelixLaughing,
        angry: FelixAngry,
        sad: FelixSad,
      },
      tabItems: [
        { key: 'sistema', label: 'Sistema', icon: 'mdi-message-text' },
        { key: 'manuais', label: 'Manuais', icon: 'mdi-book-open-page-variant' },
        { key: 'pops', label: "POP's", icon: 'mdi-file-document' },
      ],
      currentExpression: 'neutral',
      isTyping: false,
      currentToolStatus: null,
      showScrollToBottom: false,
      elapsedSeconds: 0,
      elapsedTimer: null,
      scrollThrottleTimer: null,
      typewriterBuffer: '',
      typewriterTimer: null,
      typewriterCharDelay: 15,
      toolStatusTimestamp: 0,
      toolStatusTimeout: null,
    }
  },

  computed: {
    currentFelixExpression() {
      return this.felixExpressions[this.currentExpression]
    },
    currentMessages() {
      return this.sessions[this.getCurrentTabType()].messages
    },
    currentChatId() {
      return this.sessions[this.getCurrentTabType()].chatId
    },
    currentFelixVideoKey() {
      const tabType = this.getCurrentTabType()
      if (this.aiUsage?.canUseAi === false) return 'sleeping'
      if (this.sessions[tabType].messages.length === 0 && !this.isProcessing) return 'presentation'
      if (this.isProcessing || this.isTyping) return 'searching'
      if (this.isIdle) return 'sleeping'
      return 'neutral'
    },
    currentFelixVideo() {
      return this.felixVideos[this.currentFelixVideoKey]
    },
    felixStateKey() {
      if (this.aiUsage?.canUseAi === false) return 'sleeping'
      if (this.isProcessing || this.isTyping) return 'searching'
      if (this.isIdle) return 'sleeping'
      return 'neutral'
    },
    hasStreamingContent() {
      const msgs = this.currentMessages
      if (msgs.length === 0) return false
      const last = msgs[msgs.length - 1]
      return last.isStreaming && last.content.length > 0
    },
    felixStateKeyForMessages() {
      const tabType = this.getCurrentTabType()
      if (this.sessions[tabType].messages.length === 0 && !this.isProcessing) return 'presentation'
      if (this.isProcessing || this.isTyping) return 'searching'
      if (this.isIdle) return 'sleeping'
      return 'neutral'
    },
    activeVec() {
      return this.vecDocType === 0 ? this.popVec : this.manualVec
    },
    vecLoading() {
      return this.activeVec.loading
    },
    vecReprocessing() {
      return this.activeVec.reprocessing
    },
    vecResetting() {
      return this.activeVec.resetting
    },
    currentlyProcessingDoc() {
      const docs = this.activeVec.status.documents || []
      return docs.find(d => d.status === 2) || null
    },
    vecStatus() {
      return this.activeVec.status
    },
    vecFilterOptions() {
      const s = this.activeVec.status
      return [
        { key: 'all', label: 'Todos', count: s.total },
        { key: 'processing', label: 'Em processo', count: s.processing + s.pending },
        { key: 'completed', label: 'Prontos', count: s.completed },
        { key: 'pending', label: 'Pendentes', count: s.none + s.failed },
        { key: 'notsupported', label: 'Não suportado', count: s.notSupported },
      ]
    },
    canShowManuais() {
      return !!this.institution?.showManuais
    },
    canShowPops() {
      if (!this.institution) return false
      const types = this.institution.departmentTypes || []
      return (types.includes(0) && !!this.institution.showEngClinic) ||
             (types.includes(1) && !!this.institution.showEngInfra)
    },
    visibleTabItems() {
      return this.tabItems
        .map((tab, i) => ({ ...tab, index: i }))
        .filter(tab =>
          tab.key === 'sistema' ||
          (tab.key === 'manuais' && this.canShowManuais) ||
          (tab.key === 'pops' && this.canShowPops)
        )
    },
    filteredVecDocuments() {
      const docs = this.activeVec.status.documents || []
      switch (this.vecFilter) {
        case 1: return docs.filter(d => d.status === 1 || d.status === 2)
        case 2: return docs.filter(d => d.status === 3)
        case 3: return docs.filter(d => d.status === 0 || d.status === 4)
        case 4: return docs.filter(d => d.status === 5)
        default: return docs
      }
    },
  },

  async created() {
    this.userLogged = JSON.parse(localStorage.getItem('user'))
    this.institutionId = JSON.parse(localStorage.getItem('institution')).id
    this.institution = JSON.parse(localStorage.getItem('institution'))

    if (this.activeTab === 1 && !this.canShowManuais) this.activeTab = 0
    if (this.activeTab === 2 && !this.canShowPops) this.activeTab = 0

    this.updateInitialExpression()
    this.getAiUsage()
    this.resetIdleTimer()

    await this.fetchChatHistory()
  },

  mounted() {
    this.$nextTick(() => {
      const container = this.$refs.messagesContainer
      if (container) {
        container.addEventListener('scroll', this.onMessagesScroll)
      }
      this.initCardPosition()
    })
  },

  beforeDestroy() {
    const container = this.$refs.messagesContainer
    if (container) {
      container.removeEventListener('scroll', this.onMessagesScroll)
    }
    document.removeEventListener('mousemove', this.onDragMove)
    document.removeEventListener('mouseup', this.onDragEnd)
    document.removeEventListener('touchmove', this.onDragMove)
    document.removeEventListener('touchend', this.onDragEnd)
    if (this.elapsedTimer) clearInterval(this.elapsedTimer)
    if (this.typewriterTimer) clearTimeout(this.typewriterTimer)
    if (this.toolStatusTimeout) clearTimeout(this.toolStatusTimeout)
    if (this.idleTimeout) clearTimeout(this.idleTimeout)
    if (this.typingTimeout) clearTimeout(this.typingTimeout)
  },

  watch: {
    visible(val) {
      if (val) {
        this.$nextTick(() => {
          if (!this.cardPosition) this.initCardPosition()
        })
      }
    },
    currentMessages: {
      handler() { this.updateInitialExpression() },
      immediate: true,
    },
    activeTab(val) {
      localStorage.setItem('felix_activeTab', val)
      this.scrollToBottom()
      this.updateInitialExpression()
    },
  },

  methods: {
    getCurrentTabType() {
      return ['sistema', 'manuais', 'pops'][this.activeTab]
    },

    getTabWelcomeTitle() {
      const titles = {
        sistema: 'Olá! Sou o Felix',
        manuais: 'Manuais Técnicos',
        pops: 'Procedimentos (POP)',
      }
      return titles[this.getCurrentTabType()]
    },

    getTabWelcomeMessage() {
      const messages = {
        sistema: 'Seu assistente inteligente do FixSystem. Posso consultar ordens de serviço, equipamentos, chamados e muito mais.',
        manuais: 'Consulte manuais de equipamentos médicos. Tire dúvidas sobre instalação, operação e manutenção.',
        pops: 'Acesse os Procedimentos Operacionais Padrão. Consulte orientações e boas práticas.',
      }
      return messages[this.getCurrentTabType()]
    },

    getTabPlaceholder() {
      const placeholders = {
        sistema: 'Pergunte sobre o sistema...',
        manuais: 'Consulte um manual...',
        pops: 'Busque um procedimento...',
      }
      return placeholders[this.getCurrentTabType()]
    },

    getTabSuggestions() {
      const suggestions = {
        sistema: [
          'Como abrir um novo chamado?',
          'Onde posso cadastrar um novo equipamento?',
          'Quantas ordens de serviço foram fechadas esse mês?',
        ],
        manuais: [
          'Quais equipamentos do meu parque tecnológico você possui manual?',
          'Poderia me dar um exemplo de manual?',
          'De quais marcas você tem manuais disponíveis?',
        ],
        pops: [
          'Como funciona a criticidade dos equipamentos?',
          'Qual a metodologia para a preventiva de equipamentos?',
          'Os indicadores funcionam de que maneira?',
        ],
      }
      return suggestions[this.getCurrentTabType()] || suggestions.sistema
    },

    useSuggestion(text) {
      this.currentMessage = text
      this.sendMessage()
    },

    shouldShowAvatar(index) {
      // Show avatar only for first message or when previous message is from a different role
      if (index === 0) return true
      const msgs = this.currentMessages
      return msgs[index - 1]?.role !== 'Assistant'
    },

    formatRelativeDate(date) {
      if (!date) return ''
      const m = moment(date)
      const now = moment()
      const diffMin = now.diff(m, 'minutes')
      const diffHours = now.diff(m, 'hours')
      const diffDays = now.diff(m, 'days')

      if (diffMin < 1) return 'Agora'
      if (diffMin < 60) return `${diffMin} min atrás`
      if (diffHours < 24 && m.isSame(now, 'day')) return `Hoje às ${m.format('HH:mm')}`
      if (diffDays < 1 || (diffDays === 1 && m.isSame(now.clone().subtract(1, 'day'), 'day'))) return `Ontem às ${m.format('HH:mm')}`
      if (diffDays < 7) return `${m.format('dddd')} às ${m.format('HH:mm')}`
      if (m.isSame(now, 'year')) return m.format('DD/MM [às] HH:mm')
      return m.format('DD/MM/YYYY [às] HH:mm')
    },

    startTypewriter(tabType, assistantMessage) {
      if (this.typewriterTimer) return

      const tick = () => {
        if (this.typewriterBuffer.length === 0) {
          this.typewriterTimer = null
          return
        }

        const charsToConsume = Math.min(
          this.typewriterBuffer.length,
          Math.random() < 0.3 ? 3 : 1
        )
        const chars = this.typewriterBuffer.slice(0, charsToConsume)
        this.typewriterBuffer = this.typewriterBuffer.slice(charsToConsume)

        assistantMessage.content += chars
        this.$set(
          this.sessions[tabType].messages,
          this.sessions[tabType].messages.length - 1,
          { ...assistantMessage }
        )
        this.scrollToBottom()

        this.typewriterTimer = setTimeout(tick, this.typewriterCharDelay)
      }

      this.typewriterTimer = setTimeout(tick, this.typewriterCharDelay)
    },

    flushTypewriter(tabType, assistantMessage) {
      if (this.typewriterTimer) {
        clearTimeout(this.typewriterTimer)
        this.typewriterTimer = null
      }
      if (this.typewriterBuffer.length > 0) {
        assistantMessage.content += this.typewriterBuffer
        this.typewriterBuffer = ''
        this.$set(
          this.sessions[tabType].messages,
          this.sessions[tabType].messages.length - 1,
          { ...assistantMessage }
        )
      }
    },

    onMessagesScroll() {
      const container = this.$refs.messagesContainer
      if (!container) return
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
      this.showScrollToBottom = distanceFromBottom > 100
    },

    async onTabChange() {
      this.changeFelixExpression('neutral', false)
      this.scrollToBottom()
      this.fetchChatHistory()
    },

    getAiUsage() {
      const institution = JSON.parse(localStorage.getItem('institution') || '{}')
      this.apiService
        .get(`ai/usage/${institution.id}`)
        .then((response) => {
          if (response.statusCode === 200) {
            const { limite, canUseAi, messageCount } = response.content
            this.aiUsage = { limite, canUseAi, messageCount }
          }
        })
        .catch(() => {})
    },

    async fetchChatHistory() {
      this.loadingChats = true
      try {
        const response = await this.apiService.get(
          `ai/chats/${this.institutionId}?page=1&pageSize=50`
        )
        if (response.statusCode === 200 && response.content) {
          const chats = response.content

          // Sort by most recent first (lastMessageAt, then createdAt as fallback)
          const sorted = chats.sort((a, b) => {
            const dateA = new Date(a.lastMessageAt || a.createdAt)
            const dateB = new Date(b.lastMessageAt || b.createdAt)
            return dateB - dateA
          })

          this.chatHistory.sistema = sorted.filter((c) => c.intentType === 'System')
          this.chatHistory.manuais = sorted.filter((c) => c.intentType === 'Manual')
          this.chatHistory.pops = sorted.filter((c) => c.intentType === 'POP')
        }
      } catch (error) {
        console.error('Erro ao buscar histórico de chats', error)
      } finally {
        this.loadingChats = false
      }
    },

    changeFelixExpression(expression) {
      this.currentExpression = expression
    },

    onIntroVideoLoaded(event) {
      event.target.play().catch(() => { this.showIntroVideo = false })
    },

    detectUserTyping() {
      this.resetIdleTimer()
      this.isTyping = true
      if (this.typingTimeout) clearTimeout(this.typingTimeout)
      this.typingTimeout = setTimeout(() => {
        this.isTyping = false
        this.resetIdleTimer()
      }, 1200)
    },

    updateInitialExpression() {
      if (this.currentMessages.length === 0 && !this.isProcessing) {
        this.currentExpression = 'neutral'
      }
    },

    async sendMessage() {
      if (!this.currentMessage.trim() || this.isProcessing) return

      this.resetIdleTimer()

      const tabType = this.getCurrentTabType()
      const chatId = this.sessions[tabType].chatId

      const userMessage = {
        id: crypto?.randomUUID?.() || `${Date.now()}-u`,
        role: 'User',
        content: this.currentMessage,
        createdAt: new Date().toISOString(),
      }
      this.sessions[tabType].messages.push(userMessage)

      const messageToSend = this.currentMessage
      this.currentMessage = ''
      this.scrollToBottom()

      this.isProcessing = true
      this.currentToolStatus = null
      this.typewriterBuffer = ''
      this.changeFelixExpression('thinking')
      const startTime = Date.now()

      this.elapsedSeconds = 0
      if (this.elapsedTimer) clearInterval(this.elapsedTimer)
      this.elapsedTimer = setInterval(() => {
        this.elapsedSeconds = Math.floor((Date.now() - startTime) / 1000)
      }, 1000)

      const assistantMessage = {
        id: crypto?.randomUUID?.() || `${Date.now()}-a`,
        role: 'Assistant',
        content: '',
        createdAt: new Date().toISOString(),
        isStreaming: true,
      }
      this.sessions[tabType].messages.push(assistantMessage)
      this.scrollToBottom()

      try {
        const token = localStorage.getItem('token')
        const baseUrl = process.env.VUE_APP_BASE_URL

        const response = await fetch(`${baseUrl}ai/send-message-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            message: messageToSend,
            userId: this.userLogged.id,
            institutionId: this.institutionId,
            intentType: this.getIntentType(tabType),
            chatId: chatId,
          }),
        })

        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop()

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue

            try {
              const event = JSON.parse(line.slice(6))

              switch (event.type) {
                case 'content_delta':
                  this.typewriterBuffer += event.content
                  this.startTypewriter(tabType, assistantMessage)
                  break

                case 'tool_start':
                  this.currentToolStatus = event.label || event.tool
                  this.toolStatusTimestamp = Date.now()
                  if (this.toolStatusTimeout) {
                    clearTimeout(this.toolStatusTimeout)
                    this.toolStatusTimeout = null
                  }
                  break

                case 'tool_end':
                  // Keep the status visible for at least 1.5s so user can read it
                  {
                    const elapsed = Date.now() - (this.toolStatusTimestamp || 0)
                    const minDisplayMs = 1500
                    const remaining = Math.max(0, minDisplayMs - elapsed)
                    this.toolStatusTimeout = setTimeout(() => {
                      this.currentToolStatus = null
                      this.toolStatusTimeout = null
                    }, remaining)
                  }
                  break

                case 'chat_created':
                  this.sessions[tabType].chatId = event.chatId
                  await this.fetchChatHistory()
                  break

                case 'done':
                  this.flushTypewriter(tabType, assistantMessage)
                  assistantMessage.isStreaming = false
                  assistantMessage.processingTimeMs = event.processingTimeMs || (Date.now() - startTime)
                  assistantMessage.inputTokens = event.inputTokens
                  assistantMessage.outputTokens = event.outputTokens
                  assistantMessage.cost = event.cost
                  assistantMessage.messageId = event.messageId
                  this.$set(
                    this.sessions[tabType].messages,
                    this.sessions[tabType].messages.length - 1,
                    { ...assistantMessage }
                  )
                  this.lastProcessingTime = assistantMessage.processingTimeMs
                  break

                case 'error':
                  this.flushTypewriter(tabType, assistantMessage)
                  assistantMessage.content = event.message || 'Erro ao processar mensagem'
                  assistantMessage.isError = true
                  assistantMessage.isStreaming = false
                  assistantMessage.originalMessage = messageToSend
                  this.$set(
                    this.sessions[tabType].messages,
                    this.sessions[tabType].messages.length - 1,
                    { ...assistantMessage }
                  )
                  break
              }
            } catch (parseErr) {
              console.warn('Failed to parse SSE event:', line, parseErr)
            }
          }
        }

        this.changeFelixExpression('happy')
        setTimeout(() => this.changeFelixExpression('neutral'), 2000)

      } catch (error) {
        console.error('Streaming error:', error)
        this.flushTypewriter(tabType, assistantMessage)

        assistantMessage.content = 'Erro ao enviar mensagem. Tente novamente.'
        assistantMessage.isError = true
        assistantMessage.isStreaming = false
        assistantMessage.originalMessage = messageToSend
        this.$set(
          this.sessions[tabType].messages,
          this.sessions[tabType].messages.length - 1,
          { ...assistantMessage }
        )

        this.changeFelixExpression('scared')
        setTimeout(() => this.changeFelixExpression('neutral'), 3000)
      } finally {
        this.isProcessing = false
        this.currentToolStatus = null
        if (this.toolStatusTimeout) {
          clearTimeout(this.toolStatusTimeout)
          this.toolStatusTimeout = null
        }
        this.typewriterBuffer = ''
        if (this.typewriterTimer) {
          clearTimeout(this.typewriterTimer)
          this.typewriterTimer = null
        }
        this.elapsedSeconds = 0
        if (this.elapsedTimer) {
          clearInterval(this.elapsedTimer)
          this.elapsedTimer = null
        }
        this.resetIdleTimer()
        this.getAiUsage()
      }
    },

    retryMessage(originalMessage) {
      const tabType = this.getCurrentTabType()
      this.sessions[tabType].messages.pop()
      this.currentMessage = originalMessage
      this.sendMessage()
    },

    getIntentType(tabType) {
      return { sistema: 0, manuais: 1, pops: 2 }[tabType] || 0
    },

    scrollToBottom(force = false) {
      if (!force && this.scrollThrottleTimer) return
      this.scrollThrottleTimer = setTimeout(() => { this.scrollThrottleTimer = null }, 80)
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) container.scrollTop = container.scrollHeight
      })
    },

    initCardPosition() {
      const card = this.$el
      if (!card || !card.offsetHeight) return
      const margin = 12
      const top = window.innerHeight - card.offsetHeight - margin
      const left = window.innerWidth - card.offsetWidth - margin
      this.cardPosition = { top, left }
      card.style.top = top + 'px'
      card.style.left = left + 'px'
      card.style.bottom = 'auto'
      card.style.right = 'auto'
    },

    onDragStart(e) {
      // Don't drag when clicking interactive elements
      const target = e.target
      if (target.closest('button, .v-btn, .v-icon, .v-menu, .v-list, input, textarea, .v-chip, .seg-tab')) return

      const card = this.$el
      const rect = card.getBoundingClientRect()
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const clientY = e.touches ? e.touches[0].clientY : e.clientY

      // Initialize position if not yet set
      if (!this.cardPosition) {
        this.cardPosition = { top: rect.top, left: rect.left }
      }

      this.dragOffset = {
        x: clientX - rect.left,
        y: clientY - rect.top,
      }
      this._dragMoved = false
      this._dragPending = true

      document.addEventListener('mousemove', this.onDragMove)
      document.addEventListener('mouseup', this.onDragEnd)
      document.addEventListener('touchmove', this.onDragMove, { passive: false })
      document.addEventListener('touchend', this.onDragEnd)
    },

    onDragMove(e) {
      if (!this._dragPending && !this.isDragging) return

      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const clientY = e.touches ? e.touches[0].clientY : e.clientY

      // Only start actual drag after moving 4px (avoids blocking clicks)
      if (this._dragPending && !this.isDragging) {
        const dx = clientX - (this.dragOffset.x + this.cardPosition.left)
        const dy = clientY - (this.dragOffset.y + this.cardPosition.top)
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return
        this._dragPending = false
        this.isDragging = true
        const card = this.$el
        card.classList.add('is-dragging')
        card.classList.remove('snap-animating')
      }

      if (e.touches) e.preventDefault()
      this._dragMoved = true
      const card = this.$el

      const newLeft = clientX - this.dragOffset.x
      const newTop = clientY - this.dragOffset.y

      // Clamp to viewport (minTop = 75px to stay below app navbar)
      const navbarHeight = 75
      const margin = 12
      const maxLeft = window.innerWidth - card.offsetWidth
      const maxTop = window.innerHeight - card.offsetHeight - margin
      const left = Math.max(0, Math.min(newLeft, maxLeft))
      const top = Math.max(navbarHeight + margin, Math.min(newTop, maxTop))

      this.cardPosition = { top, left }
      card.style.top = top + 'px'
      card.style.left = left + 'px'
    },

    onDragEnd() {
      const wasDragging = this.isDragging
      this.isDragging = false
      this._dragPending = false

      document.removeEventListener('mousemove', this.onDragMove)
      document.removeEventListener('mouseup', this.onDragEnd)
      document.removeEventListener('touchmove', this.onDragMove)
      document.removeEventListener('touchend', this.onDragEnd)

      if (!wasDragging) return

      const card = this.$el
      card.classList.remove('is-dragging')

      if (!this._dragMoved) return

      this.snapToNearestCorner(card)
    },

    snapToNearestCorner(card) {
      const margin = 12
      const navbarHeight = 75
      const minTop = navbarHeight + margin
      const cw = card.offsetWidth
      const ch = card.offsetHeight
      const cx = this.cardPosition.left + cw / 2
      const cy = this.cardPosition.top + ch / 2

      const corners = [
        { top: minTop, left: margin },
        { top: minTop, left: window.innerWidth - cw - margin },
        { top: window.innerHeight - ch - margin, left: margin },
        { top: window.innerHeight - ch - margin, left: window.innerWidth - cw - margin },
      ]

      // Find nearest corner by center distance
      let nearest = corners[0]
      let minDist = Infinity
      for (const corner of corners) {
        const dx = cx - (corner.left + cw / 2)
        const dy = cy - (corner.top + ch / 2)
        const dist = dx * dx + dy * dy
        if (dist < minDist) {
          minDist = dist
          nearest = corner
        }
      }

      this.cardPosition = nearest
      card.classList.add('snap-animating')
      card.style.top = nearest.top + 'px'
      card.style.left = nearest.left + 'px'

      const onEnd = () => {
        card.classList.remove('snap-animating')
        card.removeEventListener('transitionend', onEnd)
      }
      card.addEventListener('transitionend', onEnd)
    },

    async startNewSession() {
      const tabType = this.getCurrentTabType()
      this.sessions[tabType].chatId = 0
      this.sessions[tabType].messages = []
      this.currentMessage = ''
      this.showSessions = false
      this.changeFelixExpression('neutral')
    },

    async loadSession(session) {
      try {
        const tabType = this.getCurrentTabType()
        this.sessions[tabType].chatId = session.chatId
        this.showSessions = false

        const response = await this.apiService.get(
          `ai/chat/${session.chatId}/messages?page=1&pageSize=100`
        )

        if (response.statusCode === 200 && response.content) {
          const messages = response.content.messages || []

          this.sessions[tabType].messages = messages.map((m) => ({
            id: m.id || crypto?.randomUUID?.() || `${Date.now()}-${m.role}`,
            role: m.role === 'user' ? 'User' : 'Assistant',
            content: m.content,
            createdAt: m.createdAt,
            processingTimeMs: m.processingTimeMs,
            tokensUsed: m.tokensUsed,
            inputTokens: m.inputTokens,
            outputTokens: m.outputTokens,
            cost: m.cost,
          }))

          this.scrollToBottom()
        }
      } catch (error) {
        console.error('Erro ao carregar conversa', error)
      }
    },

    resetIdleTimer() {
      this.isIdle = false
      if (this.idleTimeout) clearTimeout(this.idleTimeout)
      this.idleTimeout = setTimeout(() => {
        if (!this.isProcessing && !this.isTyping) this.isIdle = true
      }, this.idleMs)
    },

    async openVectorizationPanel() {
      this.showVectorizationPanel = true
      this.vecFilter = 0
      await Promise.all([this.fetchPopVecStatus(), this.fetchManualVecStatus()])
    },

    async fetchPopVecStatus() {
      this.popVec.loading = true
      try {
        const response = await this.apiService.get('PopDocument/vectorization-status')
        if (response.statusCode === 200 && response.content) {
          this.popVec.status = response.content
        }
      } catch (error) {
        console.error('Erro ao buscar status de vetorização POPs', error)
      } finally {
        this.popVec.loading = false
      }
    },

    async fetchManualVecStatus() {
      this.manualVec.loading = true
      try {
        const response = await this.apiService.get('EquipmentManualDocument/vectorization-status')
        if (response.statusCode === 200 && response.content) {
          this.manualVec.status = response.content
        }
      } catch (error) {
        console.error('Erro ao buscar status de vetorização Manuais', error)
      } finally {
        this.manualVec.loading = false
      }
    },

    async refreshActiveVecStatus() {
      if (this.vecDocType === 0) await this.fetchPopVecStatus()
      else await this.fetchManualVecStatus()
    },

    switchVecDocType(type) {
      this.vecDocType = type
      this.vecFilter = 0
    },

    async reprocessDocuments() {
      const vec = this.activeVec
      vec.reprocessing = true
      try {
        const endpoint = this.vecDocType === 0 ? 'PopDocument/vectorize-existing' : 'EquipmentManualDocument/vectorize-existing'
        await this.apiService.post(endpoint)
        await this.refreshActiveVecStatus()
      } catch (error) {
        console.error('Erro ao reprocessar documentos', error)
      } finally {
        vec.reprocessing = false
      }
    },

    async resetProcessing() {
      const vec = this.activeVec
      vec.resetting = true
      try {
        const endpoint = this.vecDocType === 0 ? 'PopDocument/reset-processing' : 'EquipmentManualDocument/reset-processing'
        await this.apiService.post(endpoint)
        await this.refreshActiveVecStatus()
      } catch (error) {
        console.error('Erro ao resetar processamento', error)
      } finally {
        vec.resetting = false
      }
    },

    async vectorizeSingleDoc(doc) {
      this.$set(doc, '_loading', true)
      try {
        const endpoint = this.vecDocType === 0 ? `PopDocument/vectorize/${doc.id}` : `EquipmentManualDocument/vectorize/${doc.id}`
        await this.apiService.post(endpoint)
        await this.refreshActiveVecStatus()
      } catch (error) {
        console.error('Erro ao processar documento', error)
      } finally {
        this.$set(doc, '_loading', false)
      }
    },

    vecStatusIcon(status) {
      return {
        0: 'mdi-help-circle-outline',
        1: 'mdi-clock-outline',
        2: 'mdi-cog',
        3: 'mdi-check-circle',
        4: 'mdi-alert-circle',
        5: 'mdi-file-cancel-outline',
      }[status] || 'mdi-help-circle-outline'
    },

    vecStatusColor(status) {
      return {
        0: 'grey',
        1: 'orange darken-1',
        2: 'blue',
        3: 'green',
        4: 'red',
        5: 'grey',
      }[status] || 'grey'
    },

    vecStatusLabel(status) {
      return {
        0: 'Não processado',
        1: 'Na fila',
        2: 'Processando',
        3: 'Pronto para IA',
        4: 'Erro',
        5: 'Formato não suportado',
      }[status] || 'Desconhecido'
    },
  },
}
</script>
